import { useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";

import { useDashboardFilterOptions, useSelectedDashboardFilters } from "../../../../hooks";

import { dispatch } from "../../../../state/store";
import {
  setEventsRefresh,
  setGroup,
  setIsDashboardFilterAppChanged,
  setMarketEventsRefresh,
  setSelectedGroup
} from "../../../../state/ducks";
import { arrayUtil } from "../../../../utils";
import { useSelector } from "react-redux";

import { FiltersInlineView, FiltersView } from "./FiltersView";

export const FiltersList = ({
  isLoading,
  isMarketAdminOnly,
  isInlineView,

  onUpdateDevice,
  onUpdateLocation,
  onUpdatePlatform,
  onUpdateMarket,
  onUpdateBrowser,
  onUpdateDateRange,
  onUpdateAppType,
  onUpdateGroup,
  onUpdateConnectivity
}) => {
  const { marketOptions, locationOptions, groupsOptions } = useDashboardFilterOptions();

  const { selectedMarkets, selectedAppType, selectedGroup } = useSelectedDashboardFilters();

  const group = useSelector((store) => store.dashboardFilters.group);
  const isDashboardAppFilterChanged = useSelector(
    (store) => store.dashboardFilters.isDashboardAppFilterChanged
  );

  const prevGroupsOptions = useRef(groupsOptions);

  useEffect(() => {
    if (!groupsOptions.length && selectedAppType?.length) {
      dispatch(setSelectedGroup([]));
    }
  }, [groupsOptions.length, selectedAppType?.length]);

  useEffect(() => {
    if (marketOptions?.length === 1 && selectedMarkets?.some((market) => market.id === 0)) {
      onUpdateMarket(marketOptions[0].id, [], marketOptions);
    }
  }, [marketOptions?.length]);

  useEffect(() => {
    if (
      groupsOptions.length &&
      !arrayUtil.arrayEquals(
        groupsOptions.map((g) => g.id),
        prevGroupsOptions.current.map((g) => g.id)
      )
    ) {
      dispatch(setSelectedGroup(groupsOptions));
      prevGroupsOptions.current = groupsOptions;
    }

    if (
      group?.length !== groupsOptions.length &&
      group?.some((option) => option?.id === 0) &&
      isDashboardAppFilterChanged
    ) {
      dispatch(setSelectedGroup(groupsOptions));
      dispatch(setGroup(groupsOptions));
      prevGroupsOptions.current = groupsOptions;

      dispatch(setEventsRefresh(true));
      dispatch(setMarketEventsRefresh(true));
      dispatch(setIsDashboardFilterAppChanged(false));
    }
  }, [selectedAppType?.length]);

  useEffect(() => {
    if (selectedGroup?.some((option) => option.id === 0) && groupsOptions.length) {
      dispatch(setSelectedGroup(groupsOptions));
    }
  }, [groupsOptions.length]);

  const isMarketSelectorDisabled = useMemo(() => {
    return (
      isLoading ||
      selectedAppType?.length > 2 ||
      (selectedAppType?.length === 2 && !selectedAppType?.some((appType) => appType.id === 0))
    );
  }, [isLoading, selectedAppType]);

  const isLocationSelectorDisabled = useMemo(() => {
    return (
      isLoading ||
      selectedAppType?.length > 2 ||
      (selectedAppType?.length === 2 && !selectedAppType?.some((appType) => appType.id === 0)) ||
      selectedMarkets?.length > 2 ||
      (selectedMarkets?.length === 2 && !selectedMarkets?.some((market) => market.id === 0))
    );
  }, [selectedAppType, selectedMarkets, isLoading]);

  const marketDefaultOptionsId = useMemo(() => {
    return marketOptions.length === 2 ? marketOptions[1].id : 0;
  }, [marketOptions]);

  const locationsDefaultOptionsId = useMemo(() => {
    return locationOptions.length && locationOptions.length === 2 ? locationOptions[1].id : 0;
  }, [locationOptions]);

  return isInlineView ? (
    <FiltersInlineView
      isLoading={isLoading}
      isMarketAdminOnly={isMarketAdminOnly}
      onUpdateDateRange={onUpdateDateRange}
      onUpdateMarket={onUpdateMarket}
      onUpdateBrowser={onUpdateBrowser}
      onUpdateLocation={onUpdateLocation}
      onUpdateDevice={onUpdateDevice}
      onUpdatePlatform={onUpdatePlatform}
      onUpdateAppType={onUpdateAppType}
      onUpdateGroup={onUpdateGroup}
      onUpdateConnectivity={onUpdateConnectivity}
      isLocationSelectorDisabled={isLocationSelectorDisabled}
      isMarketSelectorDisabled={isMarketSelectorDisabled}
      marketDefaultOptionsId={marketDefaultOptionsId}
      locationsDefaultOptionsId={locationsDefaultOptionsId}
    />
  ) : (
    <FiltersView
      isLoading={isLoading}
      isMarketAdminOnly={isMarketAdminOnly}
      onUpdateDateRange={onUpdateDateRange}
      onUpdateMarket={onUpdateMarket}
      onUpdateBrowser={onUpdateBrowser}
      onUpdateLocation={onUpdateLocation}
      onUpdateDevice={onUpdateDevice}
      onUpdatePlatform={onUpdatePlatform}
      onUpdateAppType={onUpdateAppType}
      onUpdateGroup={onUpdateGroup}
      onUpdateConnectivity={onUpdateConnectivity}
      isLocationSelectorDisabled={isLocationSelectorDisabled}
      isMarketSelectorDisabled={isMarketSelectorDisabled}
      marketDefaultOptionsId={marketDefaultOptionsId}
      locationsDefaultOptionsId={locationsDefaultOptionsId}
    />
  );
};

FiltersList.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  isMarketAdminOnly: PropTypes.bool.isRequired,
  isInlineView: PropTypes.bool,

  onUpdateDevice: PropTypes.func.isRequired,
  onUpdateMarket: PropTypes.func.isRequired,
  onUpdateLocation: PropTypes.func.isRequired,
  onUpdatePlatform: PropTypes.func.isRequired,
  onUpdateBrowser: PropTypes.func.isRequired,
  onUpdateDateRange: PropTypes.func.isRequired,
  onUpdateAppType: PropTypes.func.isRequired,
  onUpdateGroup: PropTypes.func.isRequired,
  onUpdateConnectivity: PropTypes.func.isRequired
};

export default FiltersList;
